import { store } from "../../../redux/store";
import {
  emailVerifyURL,
  loginURL,
  recoverPasswordURL,
  registerUserURL,
  socialAuthURL,
  verificationURL,
  userURL,
} from "apiManager/APIConfig";
import { CallAPI } from "../../../apiManager/ApiCall";
import { setAuth } from "../../../redux/slices/authSlice";
import { setAllUsers } from "../../../redux/slices/userSlice";
import { Alert } from "reactstrap";

const allowedLoginRoles = (role) =>
  role == "admin" ||
  role == "prof" ||
  role == "university_admin" ||
  role == "student" ||
  role == "custom_user";

/**
 * Apple / Google-style social login — same API as the app; only sets session if role is allowed (same as `LoginCall`).
 */
export const SocialAuth = async (body) => {
  try {
    const response = await CallAPI({
      url: socialAuthURL,
      method: "POST",
      body,
    });
    if (response?.access_token && allowedLoginRoles(response?.role)) {
      store.dispatch(setAuth(response));
    }
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

/** Step 1 forgot password — matches mobile `getEmailVerificationCode(email, '', false)`. */
export const requestPasswordResetCode = async (email) => {
  const normalized = String(email || "")
    .toLowerCase()
    .trim();
  if (!normalized) {
    return { ok: false, response: { status: 400, message: "Email is required." } };
  }
  try {
    const response = await CallAPI({
      method: "GET",
      url: `${verificationURL}?email=${encodeURIComponent(
        normalized
      )}&registered=false&full_name=`,
    });
    if (response?.status === 200) {
      return { ok: true, response };
    }
    return { ok: false, response };
  } catch (error) {
    return { ok: false, response: { status: 400, message: "Network error" } };
  }
};

/** Resend OTP — matches mobile `getEmailVerificationCode(email, true)`. */
export const resendPasswordResetCode = async (email) => {
  const normalized = String(email || "")
    .toLowerCase()
    .trim();
  if (!normalized) {
    return { ok: false, response: { status: 400, message: "Email is required." } };
  }
  try {
    const response = await CallAPI({
      method: "GET",
      url: `${verificationURL}?email=${encodeURIComponent(
        normalized
      )}&registered=true&full_name=true`,
    });
    if (response?.status === 200) {
      return { ok: true, response };
    }
    return { ok: false, response };
  } catch (error) {
    return { ok: false, response: { status: 400, message: "Network error" } };
  }
};

/** POST `auth/recover_password` — same body as mobile. */
export const recoverPassword = async ({ email, code, password }) => {
  const normalized = String(email || "")
    .toLowerCase()
    .trim();
  try {
    const response = await CallAPI({
      url: recoverPasswordURL,
      method: "POST",
      body: {
        email: normalized,
        code: String(code).trim(),
        password,
      },
    });
    if (response?.status === 200) {
      return { ok: true, response };
    }
    return { ok: false, response };
  } catch (error) {
    return { ok: false, response: { status: 400 } };
  }
};

/**
 * Step 1 of the public sign-up flow: ask the API to email a 6-digit OTP.
 *
 * Mirrors the mobile contract (`getEmailVerificationCode(email, fullName)`):
 *   GET auth/verification?email=...&registered=false&full_name=...
 *
 * `registered=false` tells the API "this email is brand new, please send
 * a *signup* OTP" (vs. the password-reset flow which uses `registered=true`).
 *
 * Returns `{ ok, response }`. `ok` is true only when the API confirms the
 * mail was queued (`status === 200`). Callers should NOT proceed to the
 * OTP step on `ok === false`.
 */
export const requestSignUpOtp = async (email, fullName = "") => {
  const normalized = String(email || "")
    .toLowerCase()
    .trim();
  if (!normalized) {
    return { ok: false, response: { status: 400, message: "Email is required." } };
  }
  try {
    const response = await CallAPI({
      method: "GET",
      url: `${verificationURL}?email=${encodeURIComponent(
        normalized
      )}&registered=false&full_name=${encodeURIComponent(fullName || "")}`,
    });
    if (response?.status === 200) {
      return { ok: true, response };
    }
    return { ok: false, response };
  } catch (error) {
    return { ok: false, response: { status: 400, message: "Network error" } };
  }
};

/**
 * Step 2 of the public sign-up flow: verify the OTP the user typed.
 * Mirrors the mobile `verifyEmail(email, code)` helper.
 *
 *   GET auth/verify?email=...&code=...
 *
 * The API replies with `{ status: 200, verified: boolean }`. We surface
 * a clean boolean so the UI can branch without poking at HTTP shape.
 */
export const verifySignUpOtp = async (email, code) => {
  const normalizedEmail = String(email || "")
    .toLowerCase()
    .trim();
  const normalizedCode = String(code || "").trim();
  if (!normalizedEmail || !normalizedCode) return false;
  try {
    const response = await CallAPI({
      method: "GET",
      url: `${emailVerifyURL}?email=${encodeURIComponent(
        normalizedEmail
      )}&code=${encodeURIComponent(normalizedCode)}`,
    });
    if (response?.status === 200 && response?.verified === true) {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

/**
 * Step 3 of the public sign-up flow: create the account.
 *
 * The body shape mirrors the mobile `RegisterUserCall` payload (same
 * fields, same field names) so the BE controller handles both clients
 * with one code path. Two web-specific differences:
 *
 *   - `platform: 'web'` so analytics / segmentation can tell web sign-ups
 *     apart from app sign-ups.
 *   - `app_type: 'microlab'` and `lang` are still expected by the BE; the
 *     caller passes the dashboard's current i18n locale.
 *
 * On 201 the BE returns `{ access_token, ...user }` — same shape as
 * `LoginCall`. We dispatch `setAuth` so the Redux store transitions
 * straight into the authenticated state without an extra login round-trip.
 *
 * Returns the raw response so the caller can branch on `status`
 * (e.g. 409 = email already taken, 500 = server error) and surface a
 * specific error toast — matches how `LoginCall` already behaves.
 */
export const RegisterCall = async (body) => {
  try {
    const response = await CallAPI({
      url: registerUserURL,
      method: "POST",
      body,
    });
    if (response?.access_token) {
      store.dispatch(setAuth(response));
    }
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

/** Admin dashboard: create a Microlab user without switching the current session. */
export const AdminRegisterUserCall = async (body) => {
  try {
    return await CallAPI({
      url: "user/register",
      method: "POST",
      body,
    });
  } catch (error) {
    return { status: 400, message: "Could not create user" };
  }
};

export const LoginCall = async (body) => {
  try {
    const response = await CallAPI({ url: loginURL, method: "POST", body });
    console.log("pippo response xxxx", response);

    if (response?.access_token && allowedLoginRoles(response?.role)) {
      store.dispatch(setAuth(response));
      return response;
    } else {
      return response;
    }
  } catch (error) {
    return { status: 400 };
  }
};

export const GetAllUsersCall = async ({
  limit = 25,
  page = 1,
  search = "",
  university = "",
  occupation = "",
  country = "",
  role = "",
}) => {
  try {
    const params = new URLSearchParams({
      limit: String(Number(limit) || 25),
      page: String(Number(page) || 1),
      ...(search && { search }),
      ...(university && { university }),
      ...(occupation && { occupation }),
      ...(country && { country }),
      ...(role && { role }),
    });

    const response = await CallAPI({
      url: `${userURL}?${params.toString()}`,
      method: "GET",
    });

    if (response) {
      store.dispatch(setAllUsers(response));
      return response;
    }
    return [];
  } catch (error) {
    return { status: 400 };
  }
};

/** Admin: get one user with full stats — GET /api/user/:id */
export const GetUserByIdCall = async (userId) => {
  try {
    const response = await CallAPI({
      url: `user/${userId}`,
      method: "GET",
    });
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

/** Admin: update user status (active | inactive) — PATCH /api/user/:id/status */
export const UpdateUserStatusCall = async (userId, status) => {
  try {
    const response = await CallAPI({
      url: `user/${userId}/status`,
      method: "PATCH",
      body: { status },
    });
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

/** Admin: reset user password to default 12341234 — POST /api/user/:id/reset-password */
export const ResetUserPasswordCall = async (userId) => {
  try {
    const response = await CallAPI({
      url: `user/${userId}/reset-password`,
      method: "POST",
    });
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

/** Admin only: toggle user's earning tab — POST /api/user/:id/earning (avoids CORS issues with PATCH) */
export const UpdateUserEarningCall = async (userId, enabled_earning) => {
  try {
    const response = await CallAPI({
      url: `user/${userId}/earning`,
      method: "POST",
      body: { enabled_earning },
    });
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

/** Admin only: toggle verified badge on app profile — POST /api/user/:id/profile-verified */
export const UpdateUserProfileVerifiedCall = async (userId, profile_verified) => {
  try {
    const response = await CallAPI({
      url: `user/${userId}/profile-verified`,
      method: "POST",
      body: { profile_verified },
    });
    return response;
  } catch (error) {
    return { status: 400 };
  }
};
